import React, { FC, Suspense } from 'react';
import { Box, Text, Icon, IconSize, Spinner, SpinnerSize, useSideSheetState } from '@bechtel/gds';
import { RodRequestDetailsHeaderStyled } from './styled';
import { useLingui } from '@lingui/react';
import { useDate, ErrorBoundary, TextWithEllipsis, EllipsisPlacement } from '@bechtel/client-common';
import { Trans } from '@lingui/macro';
import { RodRequestStatusMessages } from '~/components';
import { RodRequest } from '@bechtel/types';
import { EditRodRequest } from '~/request/edit/rod-room';
import { RodRequestStatusStyled, RodRequestStatusTextStyled } from '~/request/detail/header/styled';
import { useRodRoomPermissions } from '@bechtel/client-weld-common';

export interface RodRequestDetailsHeaderProps {
  rodRequestItem: RodRequest;
}

export const RodRequestDetailsHeader: FC<RodRequestDetailsHeaderProps> = ({ rodRequestItem }) => {
  const { i18n } = useLingui();
  const { formatISODate } = useDate();
  const { visible, open, close } = useSideSheetState(false);
  const { canUpdateRodRequest } = useRodRoomPermissions(
    rodRequestItem?.welds && rodRequestItem?.welds.length > 0 ? rodRequestItem.welds[0].commodityAssignees : []
  );
  return (
    <>
      <RodRequestDetailsHeaderStyled>
        <Box
          justifyContent={{ sm: 'space-between' }}
          alignItems={{ sm: 'center' }}
          style={{ width: '100%' }}
          px={{ sm: '500' }}
          py={{ sm: '450' }}
        >
          <Box flexDirection={{ sm: 'column' }}>
            <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
              <Box css={{ maxWidth: '130px' }}>
                <TextWithEllipsis
                  size="400"
                  family="graphik"
                  color="white"
                  weight="600"
                  title={rodRequestItem?.requestNumber}
                  ellipsisPlacement={EllipsisPlacement.END}
                >
                  {rodRequestItem?.requestNumber}
                </TextWithEllipsis>
              </Box>
              <RodRequestStatusStyled status={rodRequestItem?.currentStatusI18n}>
                <RodRequestStatusTextStyled status={rodRequestItem?.currentStatusI18n}>
                  {i18n._(RodRequestStatusMessages[rodRequestItem?.currentStatusI18n])}
                </RodRequestStatusTextStyled>
              </RodRequestStatusStyled>
            </Box>
            <Box py={{ sm: '100' }} mb={{ sm: '200' }}>
              <Text size="300" family="graphik" color="white">
                <Trans>Request Date</Trans>: &nbsp;
              </Text>
              <Text weight="600" size="300" family="graphik" color="white">
                {formatISODate(rodRequestItem.pickUpTime, 'dd MMM')}
              </Text>
            </Box>
            {rodRequestItem?.weldProcedure?.procedure && (
              <Box>
                <Text size="250" color="white" style={{ opacity: '0.86' }}>
                  {rodRequestItem?.weldProcedure?.procedure}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
        <Box style={{ width: '100%' }}>
          <Box
            justifyContent={{ sm: 'center' }}
            alignItems={{ sm: 'center' }}
            p={{ sm: '450' }}
            flexGrow={{ sm: 1 }}
            css={`
              ${({ theme }) => `background: ${theme.colors.blue[600]}; cursor: pointer;`}
            `}
          >
            <Icon icon="file-spreadsheet" size={IconSize.SMALL} fillColor="white" />
            &nbsp;&nbsp;
            <Text size="300" color="white">
              <Trans>View Slip</Trans>
            </Text>
          </Box>
          {canUpdateRodRequest && (
            <Box
              justifyContent={{ sm: 'center' }}
              alignItems={{ sm: 'center' }}
              p={{ sm: '450' }}
              flexGrow={{ sm: 1 }}
              css={`
                ${({ theme }) => `background: ${theme.colors.blue[700]}; cursor: pointer;`}
              `}
              onClick={open}
            >
              <Icon icon="edit" size={IconSize.SMALL} fillColor="white" />
              &nbsp;&nbsp;
              <Text size="300" color="white">
                <Trans>Edit Request</Trans>
              </Text>
            </Box>
          )}
        </Box>
      </RodRequestDetailsHeaderStyled>
      <ErrorBoundary>
        <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
          {visible && <EditRodRequest rodRequest={rodRequestItem} onClose={close} />}
        </Suspense>
      </ErrorBoundary>
    </>
  );
};
